import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download, PieChart as PieIcon, Building2, User, Laptop, FileText, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, total }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">{value} <span className="text-muted-foreground">({pct}%)</span></span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Reports() {
  const [summary, setSummary] = useState(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // current month
  const [monthly, setMonthly] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  const load = useCallback(async () => {
    try { const r = await api.get("/assets/report/summary"); setSummary(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, []);
  const loadMonthly = useCallback(async () => {
    setLoadingMonthly(true);
    try {
      const r = await api.get("/reports/monthly", { params: { year, month } });
      setMonthly(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingMonthly(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/assets/report/csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ativos_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV baixado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const r = await api.get("/reports/monthly/pdf", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio_helpdesk_${year}_${String(month).padStart(2,"0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDownloadingPdf(false); }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try {
      const r = await api.post("/reports/monthly/send", { year, month });
      toast.success(`Enviado para ${r.data.sent} destinatário(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSendingReport(false); }
  };

  if (!summary) return null;
  const entries = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]);
  const total = summary.total || 1;

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Análise</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Relatórios de Ativos</h1>
          <p className="mt-2 text-sm text-muted-foreground">Distribuição por status, tipo, departamento e responsáveis.</p>
        </div>
        <Button onClick={downloadCsv} data-testid="download-csv-btn"><Download className="mr-2 h-4 w-4" /> Exportar CSV</Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card><CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total de ativos</div>
          <div className="mt-2 font-heading text-4xl font-semibold">{summary.total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Atribuídos</div>
          <div className="mt-2 font-heading text-4xl font-semibold text-emerald-600">{summary.assigned}</div>
        </CardContent></Card>
        <Card><CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Sem responsável</div>
          <div className="mt-2 font-heading text-4xl font-semibold text-amber-600">{summary.unassigned}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><PieIcon className="h-4 w-4" /> Por status</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {entries(summary.by_status).map(([k, v]) => <Row key={k} label={k} value={v} total={total} />)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Laptop className="h-4 w-4" /> Por tipo</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {entries(summary.by_type).map(([k, v]) => <Row key={k} label={k} value={v} total={total} />)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Building2 className="h-4 w-4" /> Por departamento</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {entries(summary.by_department).map(([k, v]) => <Row key={k} label={k} value={v} total={total} />)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><User className="h-4 w-4" /> Por responsável</CardTitle></CardHeader>
          <CardContent className="max-h-80 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Funcionário</TableHead><TableHead className="text-right">Ativos</TableHead></TableRow></TableHeader>
              <TableBody>
                {entries(summary.by_assignee).map(([k, v]) => (
                  <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono">{v}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* ---------- Monthly report ---------- */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 font-heading text-lg"><FileText className="h-4 w-4" /> Relatório mensal</CardTitle>
              <CardDescription>Consolidado de chamados e ativos do período — exporte PDF ou envie por e-mail.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="w-40" data-testid="monthly-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" className="w-24" value={year} min="2020" max="2100"
                onChange={(e) => setYear(Number(e.target.value))} data-testid="monthly-year-input" />
              <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="monthly-download-pdf-btn">
                {downloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Baixar PDF
              </Button>
              <Button onClick={sendReport} disabled={sendingReport} data-testid="monthly-send-btn">
                {sendingReport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Enviar por e-mail
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingMonthly ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : monthly ? (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Abertos</div>
                  <div className="mt-2 font-heading text-3xl font-semibold text-primary">{monthly.tickets.opened}</div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Resolvidos</div>
                  <div className="mt-2 font-heading text-3xl font-semibold text-emerald-600">{monthly.tickets.resolved}</div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Em aberto</div>
                  <div className="mt-2 font-heading text-3xl font-semibold text-amber-600">{monthly.tickets.still_open}</div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">TMR (horas)</div>
                  <div className="mt-2 font-heading text-3xl font-semibold">{monthly.tickets.avg_resolution_hours}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">Chamados resolvidos por técnico</h3>
                  <Table>
                    <TableHeader><TableRow><TableHead>Técnico</TableHead><TableHead className="text-right">Resolvidos</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {Object.keys(monthly.tickets.by_technician).length === 0 ? (
                        <TableRow><TableCell colSpan={2} className="text-muted-foreground">Sem dados no período</TableCell></TableRow>
                      ) : Object.entries(monthly.tickets.by_technician).sort((a,b) => b[1]-a[1]).map(([k,v]) => (
                        <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono">{v}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div>
                  <h3 className="mb-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">Chamados por prioridade</h3>
                  <div className="space-y-3">
                    {Object.keys(monthly.tickets.by_priority).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sem dados no período</p>
                    ) : (() => {
                      const tot = Object.values(monthly.tickets.by_priority).reduce((a,b)=>a+b,0) || 1;
                      return Object.entries(monthly.tickets.by_priority).sort((a,b)=>b[1]-a[1]).map(([k,v]) => {
                        const pct = Math.round(v/tot*100);
                        return (
                          <div key={k} className="space-y-1.5">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{k}</span>
                              <span className="font-mono">{v} <span className="text-muted-foreground">({pct}%)</span></span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-4 rounded-md border border-border bg-muted/50 p-4 text-sm">
                <div><span className="text-muted-foreground">Ativos no snapshot:</span> <b>{monthly.assets.total}</b></div>
                <div><span className="text-muted-foreground">Com responsável:</span> <b>{monthly.assets.assigned}</b></div>
                <div><span className="text-muted-foreground">Movimentações no mês:</span> <b>{monthly.assets.movements_in_month}</b></div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
